// shared-lib/cabinet-runs/vanitySinkBay.js
//
// How wide a vanity's sink bays are — the ONE rule, asked for the whole run at
// once, with the sizing intent as its argument (`sizing.js`).
//
// It takes ALL the basins rather than one at a time because the answer is not
// per-basin: what a bay may claim is bounded by its NEIGHBOUR as much as by the
// run's end, and whether the leftovers are worth building is a question about
// the whole run. Asking per basin is what left a 63.6" double vanity as two 24"
// bases and three scribes — see below.
//
// Two callers, because the answer has to be decided twice: once by
// `vanityToRun` when it derives a run, and again by the depth picker when the
// user changes the intent, since a bay's width is STORED in the run's spec and
// the fill only reads it. Extracted rather than duplicated for the obvious
// reason — two copies of "what is a sink base" is how the profile's 1" and the
// scan path's 1.25" came to be two answers for one overhang.
//
// Pure. No I/O.

const {
  sinkBaseWidthFor,
  sinkBaseFromWidths,
  SINK_BASE_WIDTHS_IN,
} = require('./bays');
const { kitchenSinkBaseWidthsIn } = require('./sinkBaseWidths');
const { familyOf, familyFillOpts, narrowestModuleIn } = require('./families');
const { familySizes, vanitySinkWidthsIn } = require('./vendorSizes');
const { DOUBLE_BOWL_UNIT_WHEN_BOTH_FIT } = require('./doubleBowlUnit');
const { profileOf, VANITY_PROFILE_ID } = require('./profiles');
const { AS_BUILT, sizingOf } = require('./sizing');
const { getFixtureGroupSinks } = require('../vanity-assembly/fixtureGroup');

const IN = 0.0254;
// A sixteenth of a millimetre: two lengths the user would call the same.
const EPS = 1e-9;
/** A length as a sentence says it: 19", 20.5". */
const said = (m) => `${+(m / IN).toFixed(1)}"`;
const clamp = (v, lo, hi) => Math.min(Math.max(v, lo), hi);
/** The standard sink bases `sinkBaseWidthFor` picks from, in metres. */
const STANDARD_SINK_WIDTHS = SINK_BASE_WIDTHS_IN.map((w) => w * IN);

/**
 * Each basin's TERRITORY: the stretch of run that basin's cabinetry may claim,
 * divided from its neighbours at the MIDPOINT BETWEEN THE BOWLS (user ruling
 * 2026-09-26) and bounded by the run's own ends.
 *
 * The midpoint rather than equal shares, because it is the bowls that are
 * fixed: a scanned vanity's basins are incumbents the run never re-emits, so
 * dividing between them is the one split that cannot disagree with where the
 * water actually is.
 */
function territoriesFor(sorted, lo, hi) {
  return sorted.map((b, i) => ({
    lo: i === 0 ? lo : (sorted[i - 1].centerParam + b.centerParam) / 2,
    hi:
      i === sorted.length - 1
        ? hi
        : (b.centerParam + sorted[i + 1].centerParam) / 2,
  }));
}

/** The narrowest standard base that clears this bowl, inside this territory. */
function standardBaseIn(bowl, centerParam, territory) {
  const widestHere =
    2 * Math.min(centerParam - territory.lo, territory.hi - centerParam);
  return Math.min(
    territory.hi - territory.lo,
    Math.max(bowl, Math.min(sinkBaseWidthFor(bowl), widestHere))
  );
}

// ─── A vanity built in a cabinet LINE (2026-10-21) ──────────────────────────
//
// A line with a size catalog (Venus) sells its vanity sink bases at its own
// widths, and one unit for two bowls. A vanity the bathroom generator planned
// in such a line used to be SNAPPED to the widest one sink unit that fit
// (design 2026-10-05, D6) — an 88.7" double built as a 60" box with 14" of
// bare wall either side. It is built as a run over the whole planned width
// instead, and these are that run's sink bases; the fill makes the rest
// (drawer and door bases, fillers, one counter).

/**
 * The line a STANDARD vanity run is built in — a family with a size catalog
 * — or null: a kitchen run, an as-built vanity, a generic family.
 */
function vanityLineOf(run, sizing = sizingOf(run)) {
  if (profileOf(run).id !== VANITY_PROFILE_ID || sizing === AS_BUILT) {
    return null;
  }
  const family = familyOf(run);
  return familySizes(family) ? family : null;
}

/**
 * The sink bases (inches, narrowest first) a run picks from by itself: a
 * standard vanity's LINE sizes on a vanity run built in one, a kitchen's
 * line sizes up to 36" (`sinkBaseWidths.js`), and empty — the standard list —
 * for any other vanity.
 */
function sinkBaseWidthsForRun(run) {
  const line = vanityLineOf(run);
  if (line) return vanitySinkWidthsIn(line, 1);
  if (profileOf(run).id === VANITY_PROFILE_ID) return [];
  return kitchenSinkBaseWidthsIn(familyOf(run));
}

/**
 * The narrowest sink base that fits a bowl `bowlWidth` wide on this run:
 * `sinkBaseWidthFor` — the bowl plus `SINK_BASE_SIDE_ALLOWANCE_IN` — in the
 * LINE's own sizes on a run built in one (Venus sells a 27" vanity sink base,
 * where the standard list jumps from 24" to 30"), the widest it makes for a
 * bowl wider than all of them. ONE answer for the plan below and for the
 * fill, which widens a sink bay to fit its bowl (`bayFootprint`): two answers
 * would have the fill grow every 27" unit the plan chose into a 30" one.
 */
function sinkBaseWidthForRun(run, bowlWidth) {
  const widths = sinkBaseWidthsForRun(run);
  return widths.length
    ? sinkBaseFromWidths(bowlWidth, widths)
    : sinkBaseWidthFor(bowlWidth);
}

/** The narrowest box the fill packs into what a run's sink bases leave. */
function narrowestFillBox(run) {
  const widths = familyFillOpts(run).allowedWidthsIn;
  return (widths?.length ? Math.min(...widths) : narrowestModuleIn(run)) * IN;
}

/**
 * Both bowls in the line's double-bowl unit `width` wide, centred on them —
 * or null where they do not both fit inside it as they stand, or one is wider
 * than the half it would sit in. The unit seats each bowl in the middle of its
 * own half, which is where its compartments are.
 */
function doubleUnit(sorted, lo, hi, width) {
  if (hi - lo < width - EPS) return null;
  const mid = (sorted[0].centerParam + sorted[1].centerParam) / 2;
  const centerParam = clamp(mid, lo + width / 2, hi - width / 2);
  const inside = sorted.every(
    (b) =>
      b.centerParam - b.bowl / 2 >= centerParam - width / 2 - EPS &&
      b.centerParam + b.bowl / 2 <= centerParam + width / 2 + EPS &&
      b.bowl <= width / 2 + EPS
  );
  if (!inside) return null;
  return {
    basins: sorted,
    width,
    centerParam,
    seats: [centerParam - width / 4, centerParam + width / 4],
  };
}

/**
 * One bowl's single sink unit: the base the bowl needs in the line's sizes
 * (`sinkBaseWidthForRun`), else — where its territory cannot hold that — the
 * widest the line makes that holds the bowl at all, which is the standard
 * rule's cap. Centred on the bowl and inside its territory, as the standard
 * rule is. `{ refusedRoom }` when the line makes nothing that fits.
 */
function singleUnit(b, t, widths, run) {
  const room = 2 * Math.min(b.centerParam - t.lo, t.hi - b.centerParam);
  const fits = widths.filter((w) => w <= room + EPS && w >= b.bowl - EPS);
  if (!fits.length) return { refusedRoom: room };
  const wanted = sinkBaseWidthForRun(run, b.bowl);
  const width = fits.find((w) => w >= wanted - EPS) ?? fits[fits.length - 1];
  return {
    basins: [b],
    width,
    centerParam: b.centerParam,
    seats: [b.centerParam],
  };
}

/** The stretches a set of units leaves bare: both run ends, and between. */
function gapsBetween(units, lo, hi) {
  const edges = units.map((u) => ({
    start: u.centerParam - u.width / 2,
    end: u.centerParam + u.width / 2,
  }));
  return [
    edges[0].start - lo,
    ...edges.slice(1).map((e, i) => e.start - edges[i].end),
    hi - edges[edges.length - 1].end,
  ].filter((g) => g > EPS);
}

/**
 * The widest of `widths` (metres, narrowest first) no wider than `room` and
 * no narrower than `atLeast` — or `atLeast` itself where none is. The one
 * answer a line's unit (`grownToTerritory`) and a standard base
 * (`standardSinkBays`) grow by when nothing fits beside them.
 */
function widestHolding(widths, room, atLeast) {
  const holds = widths.filter((w) => w <= room + EPS && w >= atLeast - EPS);
  return holds.length ? holds[holds.length - 1] : atLeast;
}

/**
 * A single unit grown to the widest the line makes inside its territory,
 * still around its bowl — for a run whose every gap is narrower than any box
 * the fill can pack, where the alternative is a sink base between strips of
 * filler (a 36" single as a 27" unit and two 4.5" fillers). The as-built
 * branch's absorb, in the line's own sizes: the unit is one the line sells,
 * which is what keeps it a STANDARD answer.
 */
function grownToTerritory(unit, t, widths) {
  const b = unit.basins[0];
  const width = widestHolding(widths, t.hi - t.lo, unit.width);
  return {
    ...unit,
    width,
    centerParam: clamp(b.centerParam, t.lo + width / 2, t.hi - width / 2),
  };
}

/**
 * The line's sink units for these bowls — `{ units }`, or `{ refusal }` when
 * it makes nothing that fits one of them.
 *
 * Two bowls go in the double-bowl unit wherever both fit inside it
 * (`doubleBowlUnit.js` holds that choice); otherwise each bowl gets a single
 * unit in its own territory, divided at the midpoint between the bowls.
 */
function lineSinkUnits({ sorted, lo, hi, run, family }) {
  if (DOUBLE_BOWL_UNIT_WHEN_BOTH_FIT && sorted.length === 2) {
    for (const wIn of vanitySinkWidthsIn(family, 2)) {
      const unit = doubleUnit(sorted, lo, hi, wIn * IN);
      if (unit) return { units: [unit] };
    }
  }
  const widths = vanitySinkWidthsIn(family, 1).map((w) => w * IN);
  const territories = territoriesFor(sorted, lo, hi);
  const units = [];
  for (const [i, b] of sorted.entries()) {
    const unit = singleUnit(b, territories[i], widths, run);
    if (!unit.basins) {
      return {
        refusal:
          `${family.vendor || family.name} makes no vanity sink base that ` +
          `fits a ${said(b.bowl)} bowl in ${said(unit.refusedRoom)}`,
      };
    }
    units.push(unit);
  }
  const gaps = gapsBetween(units, lo, hi);
  const minBox = narrowestFillBox(run);
  if (gaps.length && gaps.every((g) => g < minBox - EPS)) {
    return {
      units: units.map((u, i) => grownToTerritory(u, territories[i], widths)),
    };
  }
  return { units };
}

/**
 * The ONE sink-bay rule, with what a line said about it: `vanitySinkBays` is
 * its `bays`.
 *
 * Under a cabinet line with a size catalog (`familySizes`), a STANDARD vanity
 * takes the line's own sink units (`lineSinkUnits`): a bay per unit, one bay
 * for the double-bowl unit carrying `sinkCount: 2` (the fill stamps it on the
 * box, which is how the estimate tells the double from the single 60"). The
 * bowls are never moved by the bays: `applianceParam` is where each unit's
 * first bowl stands, on a derive and on a pick alike — one bay cannot seat two
 * bowls, so a double unit could not honour the pick's re-seat anyway. Where
 * the unit wants them is `seats` (bowl id → param): a BUILDER builds the bowls
 * there before deriving (`lineVanity.seatBasinsInLine`), so the derive finds
 * them already seated.
 *
 * Nothing the line makes fits: `refusal` says so, and the bays are the
 * standard rule's. A BUILDER asks first (`lineVanity.seatBasinsInLine`) and
 * makes no run of such a vanity at all — it stays one box in the line's
 * family, priced as missing (`build_vanity`). A run that is already in the
 * line keeps the standard bays, at sizes the line does not make, which the
 * estimate prices as missing too. Never quietly a generic vanity.
 *
 * Every other run — no line, or as-built — is exactly the rule it always was
 * (`standardSinkBays`), with no seats and no refusal.
 *
 * @returns {{ bays: Object[], seats: Object<string, number>,
 *   refusal: string|null }}
 */
function vanitySinkPlan(opts) {
  const { basins, lo, hi, sizing, run = null } = opts;
  const family = vanityLineOf(run, sizing);
  const usable = (basins || []).filter(
    (b) => b && b.bowl > 0 && Number.isFinite(b.centerParam)
  );
  if (!usable.length || !family) {
    return { bays: standardSinkBays(opts), seats: {}, refusal: null };
  }
  const sorted = [...usable].sort((a, b) => a.centerParam - b.centerParam);
  const { units, refusal } = lineSinkUnits({ sorted, lo, hi, run, family });
  if (refusal) return { bays: standardSinkBays(opts), seats: {}, refusal };
  const seats = {};
  for (const u of units) {
    u.basins.forEach((b, i) => (seats[b.id] = u.seats[i]));
  }
  return {
    bays: units.map((u) => ({
      id: u.basins[0].id,
      width: u.width,
      centerParam: u.centerParam,
      applianceParam: u.basins[0].centerParam,
      ...(u.basins.length > 1 ? { sinkCount: u.basins.length } : {}),
    })),
    seats,
    refusal: null,
  };
}

/**
 * The widths to reserve for a vanity's sink bases, and where, for a run with
 * no line (or matching what is there).
 *
 * STANDARD sizing: the narrowest standard base that clears each bowl by 3" a
 * side (`sinkBaseWidthFor` — "a sink reserves its BASE CABINET, not the bowl
 * the scan measured"), capped at what its territory can hold while staying
 * centred on the bowl, floored at the bowl itself. The cap is the part a
 * kitchen never needs: 24" is the narrowest standard base and a vanity is
 * routinely narrower. Where what those bases leave is too narrow for any
 * cabinet, each grows to the widest standard base that still stands where it
 * would — centred on its bowl, or in its half when the cabinet leads (a
 * 37.1" vanity takes a 36" base and two 0.55" fillers, not a 24" one between
 * two 6.5" strips) — the standard answer to the leftovers as-built absorbs.
 *
 * AS-BUILT sizing: if EVERY leftover gap is narrower than the narrowest module
 * the family sells, the bases absorb them — each basin takes its whole
 * territory. A scanned vanity IS its boxes, and the alternative is cabinets
 * between scribes: one basin at 36.9" gave a 24" base between two 6.5" strips,
 * and two basins at 63.6" gave two 24" bases and 7.66" + 0.26" + 7.66" of pure
 * filler without a single real cabinet. Above that threshold the run already
 * packs into real cabinets and widening would only lose them, which is why the
 * five other multi-basin corpus runs (0.7"-6.6" of filler beside 3-6 real
 * boxes) are deliberately left alone.
 *
 * Nothing the scan measured moves either way: the basins are incumbents whose
 * footprint the run reserves and never re-emits, so a widened bay changes the
 * CABINET around the bowl and not the bowl (measured — a bay widened to its
 * territory emits no basin at all).
 *
 * @param {Object} opts
 * @param {Array<{id: string, bowl: number, centerParam: number}>} opts.basins
 *   every basin on this run, in any order.
 * @param {number} opts.lo   the run's near end, in the basins' own frame.
 * @param {number} opts.hi   the run's far end.
 * @param {string} opts.sizing `sizingOf(run)`.
 * @param {Object} [opts.run]  for the family's narrowest module — and, in
 *   `vanitySinkPlan`, whether the family is a line with a size catalog; the
 *   default family when absent, which is what the fill will resolve too.
 * @param {boolean} [opts.fromPick] true when the user has just ANSWERED the
 *   sizing question (the depth picker), false when a derive is recording the
 *   vanity that already exists. Only a pick may re-seat a basin — see the
 *   "who leads" note below.
 * @returns {Array<{id: string, width: number, centerParam: number}>} one per
 *   basin, keyed by the id it was given.
 */
function standardSinkBays({
  basins,
  lo,
  hi,
  sizing,
  run = null,
  fromPick = false,
}) {
  const usable = (basins || []).filter(
    (b) => b && b.bowl > 0 && Number.isFinite(b.centerParam)
  );
  if (!usable.length) return [];
  const sorted = [...usable].sort((a, b) => a.centerParam - b.centerParam);
  const territories = territoriesFor(sorted, lo, hi);
  const bases = sorted.map((b, i) =>
    standardBaseIn(b.bowl, b.centerParam, territories[i])
  );

  // Every stretch the bases leave behind: the two run ends and each space
  // between neighbours.
  const edges = sorted.map((b, i) => ({
    start: b.centerParam - bases[i] / 2,
    end: b.centerParam + bases[i] / 2,
  }));
  const gaps = [
    edges[0].start - lo,
    ...edges.slice(1).map((e, i) => e.start - edges[i].end),
    hi - edges[edges.length - 1].end,
  ].filter((g) => g > EPS);

  const minModule = narrowestModuleIn(run) * IN;
  const nothingFitsBeside = gaps.length > 0 && gaps.every((g) => g < minModule);
  const absorb = sizing === AS_BUILT && nothingFitsBeside;
  // Who leads, the cabinet or the bowl: see the note in the map below.
  const cabinetLeads = fromPick && sizing !== AS_BUILT && sorted.length > 1;
  // STANDARD: the same leftovers, answered in standard sizes — each base
  // grows to the widest standard sink base that fits WHERE IT WILL STAND,
  // as a line's unit does (`grownToTerritory`). That is centred on its bowl,
  // as the standard rule always places a base, unless the cabinet leads (a
  // pick on a double), when it is centred in its half and the bowl follows.
  // Without it a 37.1" layout vanity was a 24" base between two 6.5" fillers
  // (two prod bathrooms, 2026-11-03). A double the builder
  // just laid out keeps its bases on its bowls: wider ones would stand off
  // them.
  const widths =
    nothingFitsBeside && !absorb
      ? bases.map((w, i) => {
          const t = territories[i];
          const c = sorted[i].centerParam;
          const room = cabinetLeads
            ? t.hi - t.lo
            : 2 * Math.min(c - t.lo, t.hi - c);
          return widestHolding(STANDARD_SINK_WIDTHS, room, w);
        })
      : bases;

  return sorted.map((b, i) => {
    const t = territories[i];
    if (absorb) {
      // The box is the whole territory, so its centre is the territory's —
      // but the BOWL keeps its own place. Seating it to the box's middle is
      // what moved a scanned double vanity's bowls 3.77" on the first
      // re-solve, as-built, which is the one thing as-built may not do.
      return {
        id: b.id,
        width: t.hi - t.lo,
        centerParam: (t.lo + t.hi) / 2,
        applianceParam: b.centerParam,
      };
    }
    // WHO LEADS, the cabinet or the bowl — the sizing intent again.
    //
    // AS-BUILT: the bowl. It is where the scan found it and the base is built
    // around it.
    //
    // STANDARD with SEVERAL basins, AND ONLY FROM A PICK: the CABINET. The run is divided between
    // the bowls, each half is one standard vanity unit, and a standard unit
    // has its base centred in its half — so the base takes the territory's
    // middle and the basin is seated to it by `moveApplianceObjectsToBays`
    // (product directive 2026-09-27: "when standard size cabinets are chosen,
    // the double vanity sinks have to reposition to match the center of each
    // sink cabinet"). Inheriting the scan's slightly-inboard bowls instead is
    // what left `D6B52714` with 7.66" gaps at both ends and a 0.26" scribe
    // between two bases crammed together in the middle — the scan's asymmetry
    // wearing a standard label.
    //
    // Three gates, and each one is a population that must not move:
    //
    //  - a SINGLE basin has no division to be centred in, and moving a lone
    //    vanity's sink to the middle of a long run is a change nobody asked
    //    for;
    //  - an AS-BUILT basin is where the scan found it, and re-seating it is
    //    the one thing "match what is there" may never do — without this gate
    //    the four double-basin corpus scans that pack into real cabinets moved
    //    their scanned bowls on a re-solve;
    //  - a DERIVE is not an answer. `build_vanity` places its own basins and
    //    is standard by definition, so without `fromPick` the derive re-seated
    //    a vanity the builder had just laid out symmetrically, 1.2" inboard.
    //
    // The directive is about the moment the user CHOOSES standard sizes for a
    // vanity that had been matching the room, which is exactly `fromPick`.
    const lead = cabinetLeads ? (t.lo + t.hi) / 2 : b.centerParam;
    // Never let that push the base past its territory.
    const half = widths[i] / 2;
    const centerParam = Math.min(Math.max(lead, t.lo + half), t.hi - half);
    // WHERE THE BOWL BELONGS, which is not always the box's middle. A box
    // widened past the bowl's own base has a centre of its own, and under
    // as-built the bowl must stay where the scan put it — so the seat is
    // stated separately and `moveApplianceObjectsToBays` prefers it to the
    // solved box centre. Under a standard PICK it is left out, which is what
    // lets the basin follow its cabinet (the product directive).
    return {
      id: b.id,
      width: widths[i],
      centerParam,
      ...(cabinetLeads ? {} : { applianceParam: b.centerParam }),
    };
  });
}

/**
 * The BOWLS a sink bay holds: its own basin (`applianceId`) and, for a line's
 * double-bowl unit (`sinkCount > 1`), the other basins of its fixture group,
 * which have no bay of their own. ONE reading for every pass that acts on a
 * bay's bowls — the vertical seat after a re-solve (`vanityBasinSeat.js`) and
 * the depth pick's re-plan — so neither can leave the second bowl behind.
 *
 * @returns {Object[]} basin objects, the bay's own first; [] for none.
 */
function bowlsOfSinkBay(bay, objects) {
  const list = objects || [];
  const own = bay?.applianceId
    ? list.find((o) => o?.id === bay.applianceId)
    : null;
  const ownList = own ? [own] : [];
  if (!(bay?.sinkCount > 1) || !bay.fixtureGroupId) return ownList;
  const others = getFixtureGroupSinks(list, bay.fixtureGroupId).filter(
    (o) => o?.id && o.id !== bay.applianceId
  );
  return [...ownList, ...others];
}

/**
 * The widths to reserve for a vanity's sink bases, and where — the ONE rule,
 * for every vanity run: `vanitySinkPlan`'s bays. See `standardSinkBays` for a
 * run with no line (or as built) and `vanitySinkPlan` for one in a line.
 */
function vanitySinkBays(opts) {
  return vanitySinkPlan(opts).bays;
}

module.exports = {
  vanitySinkBays,
  vanitySinkPlan,
  sinkBaseWidthForRun,
  bowlsOfSinkBay,
};
